/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useMemo, useState } from 'react';

import { compose } from 'recompose';

import type { InjectedRouter } from 'js/lib/connectToRouter';
import connectToRouter from 'js/lib/connectToRouter';

import { Button, Tab, TabContext, TabPanel, Tabs, breakpoints } from '@coursera/cds-core';
import { ArrowPreviousIcon } from '@coursera/cds-icons';

import LoadingSpinner from 'bundles/coursera-ui/components/basic/LoadingSpinner';
import EnableNoStatePrefetch from 'bundles/enterprise-legacy-learner-home/components/performance/EnableNoStatePrefetch';
import type Track from 'bundles/interactive-transcript/models/Track';
import type TrackList from 'bundles/interactive-transcript/models/TrackList';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import type OnDemandLectureVideosV1 from 'bundles/naptimejs/resources/onDemandLectureVideos.v1';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import type OnDemandVideosV1 from 'bundles/naptimejs/resources/onDemandVideos.v1';
import TrackedButton from 'bundles/page/components/TrackedButton';
import CourseraMetatags from 'bundles/seo/components/CourseraMetatags';
import VideoPageGoogleSchemaMarkupV2 from 'bundles/seo/components/VideoPageGoogleSchemaMarkupV2';
import { getSubdomainDetails } from 'bundles/seo/utils/domainAndMetadataUtils';
import CourseVideoOverview from 'bundles/video-logged-out-page/components/CourseVideoOverview';
import ExploreBanner from 'bundles/video-logged-out-page/components/ExploreBanner';
import VideoTranscript from 'bundles/video-logged-out-page/components/VideoTranscript';
import ContentGateContainer from 'bundles/video-logged-out-page/components/content-gates/ContentGateContainer';
import CourseContentContainer from 'bundles/video-logged-out-page/components/course-content/CourseContentContainer';
import VideoPlayerContainer from 'bundles/video-logged-out-page/components/video-player/VideoPlayerContainer';
import vlpQueriesHOC from 'bundles/video-logged-out-page/components/vlpQueriesHOC';
import { DEFAULT_VLP_META_DESCRIPTION } from 'bundles/video-logged-out-page/constants';
import type {
  Course,
  ReviewOverview,
  VLPItem,
  VLPLesson,
  VLPModule,
  VideoPlayer,
  XDPData,
} from 'bundles/video-logged-out-page/types/vlpSharedTypes';
import { constructFallBackDescriptionForVlp } from 'bundles/video-logged-out-page/utils/utils';
import { VideoTrimmingContextProvider, useVideoTrimmingContext } from 'bundles/video-trim/context/VideoTrimmingContext';
import { useSyncVideoTrimmingWithPlayer } from 'bundles/video-trim/hooks/useSyncVideoTrimmingWithPlayer';
import { getVideoTrimmedDurationSeconds, toSeconds } from 'bundles/video-trim/utils/videoTrimmingUtils';

import _t from 'i18n!nls/video-logged-out-page';

const styles = {
  wrapperStyles: css`
    .rc-VLPContainerWrapperCds {
      background: var(--cds-color-neutral-background-primary);
    }

    .rc-VLPContainerCds {
      padding-bottom: 100px;
    }

    .vlp-container-section {
      margin-bottom: 2.5rem;
    }

    .vlp-gate-cta {
      display: inline-block;
      min-height: 35px;
      min-width: 200px;
      padding: 5px 10px;
      font-weight: bold;
      border: none;
      background-color: var(--cds-color-neutral-background-primary);
      color: var(--cds-color-blue-700);
      box-shadow: 3px 3px 14px -2px rgb(0 0 0 / 35%);
    }

    .vlp-gate-content {
      color: var(--cds-color-neutral-primary-invert);

      .vlp-gate-content-title {
        margin-bottom: 20px;
        ${breakpoints.down('sm')} {
          margin-bottom: 5px;
        }
      }

      .vlp-gate-content-text {
        margin-bottom: 25px;
        ${breakpoints.down('sm')} {
          margin-bottom: 10px;
        }
      }

      .vlp-gate-content-button {
        max-width: 160px;
        min-height: 45px;
        margin-right: 45px;
        ${breakpoints.down('xs')} {
          display: block;
          min-height: 25px;
          margin-right: 0;
        }
      }

      .vlp-gate-content-dimiss-button {
        font-weight: normal;
      }

      &.in-full-overlay {
        .vlp-gate-content-title {
          margin-bottom: 25px;
          ${breakpoints.down('sm')} {
            margin-bottom: 15px;
          }
        }

        .vlp-gate-content-text {
          margin-bottom: 35px;
          ${breakpoints.down('sm')} {
            margin-bottom: 20px;
          }
        }

        .vlp-gate-content-button {
          min-height: 50px;
          max-width: 185px;
          margin-right: 45px;

          ${breakpoints.down('sm')} {
            min-height: 35px;
            margin-bottom: 10px;
          }

          ${breakpoints.down('xs')} {
            margin-right: 0;
          }
        }
      }
    }

    .vlp-main-col-content-cds {
      .rc-VideoPlayerContainer {
        .videoName {
          font-size: 20px;
          line-height: 24px;
          margin-bottom: 5px;
        }
      }
    }

    .vlp-header-flex {
      display: flex;
      flex-direction: column;
      ${breakpoints.down('sm')} {
        .vlp-header-breadcrumb-child {
          order: 1;
        }

        .vlp-header-eyebrow-child {
          order: 2;
        }
      }
    }

    .rc-VLPContainerCols {
      display: flex;
      flex-direction: row;
      justify-content: center;
      ${breakpoints.down('md')} {
        flex-direction: column;
      }

      .rc-VLPVideoPlayer {
        h1 {
          font-size: 24px;
          line-height: 17px;

          ${breakpoints.down('md')} {
            line-height: 28px;
          }
        }
      }

      .vlp-col1 {
        flex: 0 0 785px;

        @media (max-width: 1200px) {
          flex: 0 0 650px;
        }
        ${breakpoints.down('md')} {
          flex: 0 0 0;
        }
      }

      .module-desc {
        margin-bottom: 15px;
        padding-bottom: 20px;
        line-height: 22px;
        border-bottom: 2px solid var(--cds-color-grey-25);
        ${breakpoints.down('md')} {
          font-size: 14px;
          line-height: 20px;
          margin-bottom: 0;
          padding-bottom: 0;
          border-bottom: none;
        }
      }
    }

    .rc-LoadingSpinner {
      margin: 100px;
    }
  `,
  fromCourseDivider: css`
    margin: auto;
    width: 100%;
    max-width: 780px;
    margin-top: 56px;
  `,
  VLPContainerBackButtonEnabled: css`
    ${breakpoints.down('sm')} {
      padding-top: 8px;
    }
  `,
};

const TABS_VALUES = {
  COURSE: 'course',
  TRANSCRIPT: 'transcript',
} as const;

type Props = {
  video: OnDemandLectureVideosV1;
  courseSlug: string;
  itemId: string;
  course: Course;
  metaImageSrc: string;
  onDemandVideos: OnDemandVideosV1;
  tracks: TrackList;
  track: Track;
  defaultTrack: Track;
  currentModule?: VLPModule;
  currentLesson?: VLPLesson;
  currentVideoItem: VLPItem;
  nextVideoItem: VLPItem;
  lectureItems: Array<VLPItem>;
  xdpApiData: XDPData;
  gqlLoading?: boolean;
  autoPlayVideo?: boolean;
  reviewOverview: ReviewOverview;
  startAt?: number; // taken from query param, expecting time in seconds
  location: InjectedRouter['location'];
  showBackButton?: boolean;
};

export const VLPContainer = (props: Props) => {
  const { gqlLoading } = props;
  const [videoPlayer, setVideoPlayer] = useState<VideoPlayer | null>(null);
  const [videoIsReady, setVideoIsReady] = useState(false);
  const [showNextVideoCounter, setShowNextVideoCounter] = useState(false);
  const [isContentGatesActive, setIsContentGatesActive] = useState(false);
  const [checkedStatusOfContentGates, setCheckedStatusOfContentGates] = useState(false);
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const [showVideoContentGate, setShowVideoContentGate] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(TABS_VALUES.COURSE);
  const { trimmedStartTimeSeconds, trimmedEndTimeSeconds } = useVideoTrimmingContext();

  const playerEventHandlers = useSyncVideoTrimmingWithPlayer({
    player: videoPlayer,
    startTimeMs: trimmedStartTimeSeconds * 1000,
    endTimeMs: trimmedEndTimeSeconds ? trimmedEndTimeSeconds * 1000 : null,
  });

  const showBackButton = props.showBackButton || false;

  // all of the naptime queries rely on an initial graphql query to fetch xdp data, show loading if still fetching
  if (gqlLoading) return <LoadingSpinner size={64} />;

  const canAutoPlayVideo = checkedStatusOfContentGates && !isContentGatesActive && videoIsReady && props.autoPlayVideo;

  const setCounter = () => {
    if (videoPlayer) {
      videoPlayer.on('ended', () => setShowNextVideoCounter);
    }
  };

  const onPlayVideo = () => {
    setShowNextVideoCounter(false);
    setShowPlaceholder(false);

    if (videoPlayer) {
      videoPlayer.play();
      setCounter();
    }
  };

  const shouldAutoPlayVideo = () => {
    if (canAutoPlayVideo) {
      onPlayVideo();
    }
  };

  const handleActiveContentGates = (active = true) => {
    setIsContentGatesActive(active);
    setCheckedStatusOfContentGates(true);

    shouldAutoPlayVideo();
  };

  const constructSeoDescription = (
    partnerNames: string,
    courseName: string | undefined,
    moduleDescription: string | undefined
  ) => {
    const { track } = props;

    let seoDescription = moduleDescription;

    if (moduleDescription && moduleDescription === DEFAULT_VLP_META_DESCRIPTION) {
      const textTranscript = track && typeof track.getTextTranscript === 'function' ? track.getTextTranscript() : '';
      seoDescription = constructFallBackDescriptionForVlp(textTranscript);
    }

    if (partnerNames && courseName && seoDescription) {
      return `Video created by ${partnerNames} for the course "${courseName}". ${seoDescription}`;
    }
    return '';
  };

  const getSeoTags = (lectureName: string) => {
    const {
      onDemandVideos: { sources: { byResolution = undefined } = {} } = {},
      metaImageSrc,
      currentModule,
      xdpApiData: { xdpMetadata: { cdpMetadata: { name: courseName = undefined, partners = [] } = {} } = {} } = {},
      location,
    } = props;

    // Getting metatags information
    const { isInternationalSubdomain } = getSubdomainDetails(location);
    const partnerNames = partners && partners.map((partner) => partner?.name).join(', ');
    const { description: moduleDescription, name: moduleName } = currentModule || {};

    let seoTitle = lectureName;
    if (moduleName) {
      seoTitle = `${lectureName} - ${moduleName}`;
    }

    const seoTags = {
      title: seoTitle,
      description: constructSeoDescription(partnerNames, courseName, moduleDescription),
      absoluteImageHref: metaImageSrc || byResolution?.['360p']?.previewImageUrl,
      imageHref: metaImageSrc || byResolution?.['360p']?.previewImageUrl,
      disableCourseraDescriptionSuffix: true,
      disableCrawlerIndexing: isInternationalSubdomain,
    };

    return seoTags;
  };

  const setVideoTime = (time: string | number) => {
    if (videoPlayer) {
      const newTime = typeof time === 'string' ? parseFloat(time) : time;
      videoPlayer.currentTime(newTime + (trimmedStartTimeSeconds ?? 0));
    }
  };

  const getVideoPlayerObject = (vidPlayer: VideoPlayer) => {
    const { startAt } = props;
    vidPlayer.ready(() => {
      setVideoPlayer(vidPlayer);
      setVideoIsReady(true);
      shouldAutoPlayVideo();

      if (startAt) {
        setVideoTime(startAt);
      }
    });
  };

  const getVideoTime = () => {
    if (videoPlayer) {
      return videoPlayer.currentTime() || 0;
    } else {
      return 0;
    }
  };

  const {
    course,
    tracks,
    courseSlug,
    itemId,
    onDemandVideos,
    track,
    defaultTrack,
    lectureItems,
    currentLesson,
    currentModule,
    currentVideoItem,
    nextVideoItem,
    reviewOverview,
    xdpApiData: {
      xdpMetadata: {
        cdpMetadata: {
          id: courseId = undefined,
          name: courseName = undefined,
          partners = [],
          primaryS12n = undefined,
          topReviews = [],
          ratings = undefined,
          skills = [],
          instructors = [],
          totalEnrollmentCount = undefined,
        } = {},
      } = {},
    } = {},
  } = props;

  const textTranscript = track && typeof track.getTextTranscript === 'function' ? track.getTextTranscript() : '';
  const firstPartner = partners && partners[0];

  return (
    <div className="rc-VLPContainerWrapperCds" css={styles.wrapperStyles}>
      <div className="rc-VLPContainerCds">
        <div className="vlp-header-flex">
          <div className="vlp-header-eyebrow-child">
            <ContentGateContainer
              setShowVideoContentGate={setShowVideoContentGate}
              showVideoContentGate={showVideoContentGate}
              setIsContentGatesActive={handleActiveContentGates}
              videoPlayer={videoPlayer}
            />
          </div>
        </div>
        <div
          css={{
            paddingRight: '15px',
            paddingLeft: '15px',
          }}
        >
          {/* history back button */}
          {/* to show when coach recommends this page */}
          {/* we will set the url param to enable this button */}
          {showBackButton && (
            <Button
              component={TrackedButton}
              trackingName="vlp_history_back_button"
              withVisibilityTracking={false}
              requireFullyVisible={false}
              css={css`
                position: absolute;
                left: 30px;
                z-index: 10; /* to show above the video player */
              `}
              variant="ghost"
              size="small"
              icon={<ArrowPreviousIcon size="small" />}
              iconPosition="before"
              aria-label={_t('Navigate back to the previous page.')}
              data-testid="coach-back-button"
              onClick={() => {
                if (typeof window) {
                  window.history.back();
                }
              }}
            >
              {_t('Back')}
            </Button>
          )}

          <div className="rc-VLPContainerCols" css={showBackButton && styles.VLPContainerBackButtonEnabled}>
            <div className="vlp-col1">
              <div className="vlp-main-col-content-cds">
                <VideoPlayerContainer
                  courseId={courseId}
                  courseName={courseName}
                  partners={partners}
                  videoItem={currentVideoItem}
                  videoIsReady={videoIsReady}
                  getVideoPlayerObject={getVideoPlayerObject}
                  onPlayVideo={onPlayVideo}
                  onDemandVideos={onDemandVideos}
                  videoPlayer={videoPlayer}
                  tracks={tracks}
                  nextItemObj={nextVideoItem}
                  showContentGateOverlay={showVideoContentGate}
                  showNextVideoCounter={showNextVideoCounter}
                  getVideoTime={getVideoTime}
                  showPlaceholder={showPlaceholder}
                  playerEventHandlers={playerEventHandlers}
                />
                <CourseVideoOverview
                  courseId={courseId}
                  courseName={courseName}
                  courseSlug={courseSlug}
                  partners={partners}
                  ratings={ratings}
                  totalReviewCount={reviewOverview?.totalReviewCount || ratings?.ratingCount}
                  totalEnrollmentCount={totalEnrollmentCount}
                  primaryS12n={primaryS12n}
                />
                <TabContext value={activeTab}>
                  <Tabs variant="section" aria-label={_t('Course details tabs')} onChange={setActiveTab}>
                    <Tab label={_t('This Course')} value={TABS_VALUES.COURSE} />

                    <Tab
                      label={_t('Video Transcript')}
                      value={TABS_VALUES.TRANSCRIPT}
                      data-testid="video-transcript-tab"
                    />
                  </Tabs>

                  <TabPanel value={TABS_VALUES.COURSE}>
                    <CourseContentContainer
                      instructors={instructors}
                      ratings={ratings}
                      skills={skills}
                      reviewOverview={reviewOverview}
                      topReviews={topReviews}
                      course={course}
                      lectureItems={lectureItems}
                      courseSlug={courseSlug}
                      itemId={itemId}
                      currentModule={currentModule}
                      currentLesson={currentLesson}
                    />
                  </TabPanel>

                  <TabPanel value={TABS_VALUES.TRANSCRIPT}>
                    <VideoTranscript
                      tracks={tracks}
                      track={track}
                      defaultTrack={defaultTrack}
                      videoPlayer={videoPlayer}
                    />
                  </TabPanel>
                </TabContext>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ExploreBanner />
      <CourseraMetatags metaNameAndProperties={getSeoTags(currentVideoItem.name)} hrefLangOverrides={() => []} />
      <VideoPageGoogleSchemaMarkupV2
        course={course}
        courseSlug={courseSlug}
        courseName={courseName}
        partner={firstPartner}
        instructors={instructors}
        videoItem={currentVideoItem}
        lesson={currentLesson}
        module={currentModule}
        onDemandVideosSpec={onDemandVideos}
        textTranscript={textTranscript}
      />
      <EnableNoStatePrefetch />
    </div>
  );
};

const VLPContainerWithTrimContext = (props: Props) => {
  const startTime = useMemo(() => toSeconds(props.video?.startMs ?? 0), [props.video?.startMs]);

  const duration = useMemo(
    () => getVideoTrimmedDurationSeconds(props.video?.startMs, props.video?.endMs),
    [props.video?.endMs, props.video?.startMs]
  );
  return (
    <VideoTrimmingContextProvider trimmedStartTimeSeconds={startTime} trimmedDurationSeconds={duration}>
      <VLPContainer {...props} />
    </VideoTrimmingContextProvider>
  );
};
export default compose<Props, Props>(
  connectToRouter((router) => {
    return {
      // history length of 2 is because new windows for most browsers will have a starting count of 2
      // for the page you manually navigate to. 1 is the home screen of the browser.
      showBackButton:
        router.location.query.backenabled === '1' && typeof window && window.history && window.history.length > 2,
    };
  }),
  vlpQueriesHOC
)(VLPContainerWithTrimContext);
